import React, { useCallback } from 'react';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import { COLOR_BORDER, RADIUS_MD } from '../theme/tokens';

/**
 * AdvancedFilter — Enterprise Design System
 *
 * A Popover panel for building multi-field filters on table data.
 * Multiple active filters are combined with AND logic.
 *
 * Props:
 *  - columns: array of { field, headerName, type } — available columns to filter
 *  - filters: array of active filter objects { field, operator, value, value2 }
 *  - onFiltersChange: callback receiving updated filters array
 *  - anchorEl: DOM element the popover anchors to (null = closed)
 *  - open: boolean controlling popover visibility
 *  - onClose: callback when popover requests to close
 *
 * Filter object shape:
 *  { field: string, operator: string, value: string, value2?: string }
 *
 * Operators: contains, equals, greaterThan, lessThan, between
 */

const OPERATORS = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'greaterThan', label: 'Greater than' },
  { value: 'lessThan', label: 'Less than' },
  { value: 'between', label: 'Between' },
];

function FilterRow({ filter, index, columns, onUpdate, onRemove }) {
  const handleFieldChange = (e) => {
    onUpdate(index, { ...filter, field: e.target.value, value: '', value2: '' });
  };

  const handleOperatorChange = (e) => {
    const newOperator = e.target.value;
    const updated = { ...filter, operator: newOperator };
    // Clear value2 if switching away from 'between'
    if (newOperator !== 'between') {
      updated.value2 = '';
    }
    onUpdate(index, updated);
  };

  const handleValueChange = (e) => {
    onUpdate(index, { ...filter, value: e.target.value });
  };

  const handleValue2Change = (e) => {
    onUpdate(index, { ...filter, value2: e.target.value });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 1,
        borderRadius: '6px',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {/* Column select */}
      <Select
        size="small"
        value={filter.field}
        onChange={handleFieldChange}
        displayEmpty
        sx={{ minWidth: 120, fontSize: '0.8125rem' }}
        aria-label={`Filter ${index + 1} column`}
      >
        <MenuItem value="" disabled>
          Column
        </MenuItem>
        {columns.map((col) => (
          <MenuItem key={col.field} value={col.field}>
            {col.headerName || col.field}
          </MenuItem>
        ))}
      </Select>

      {/* Operator select */}
      <Select
        size="small"
        value={filter.operator}
        onChange={handleOperatorChange}
        displayEmpty
        sx={{ minWidth: 110, fontSize: '0.8125rem' }}
        aria-label={`Filter ${index + 1} operator`}
      >
        <MenuItem value="" disabled>
          Operator
        </MenuItem>
        {OPERATORS.map((op) => (
          <MenuItem key={op.value} value={op.value}>
            {op.label}
          </MenuItem>
        ))}
      </Select>

      {/* Value input */}
      <TextField
        size="small"
        value={filter.value}
        onChange={handleValueChange}
        placeholder="Value"
        sx={{ minWidth: 100, '& input': { fontSize: '0.8125rem' } }}
        aria-label={`Filter ${index + 1} value`}
      />

      {/* Value2 input (for between operator) */}
      {filter.operator === 'between' && (
        <TextField
          size="small"
          value={filter.value2 || ''}
          onChange={handleValue2Change}
          placeholder="Value 2"
          sx={{ minWidth: 100, '& input': { fontSize: '0.8125rem' } }}
          aria-label={`Filter ${index + 1} second value`}
        />
      )}

      {/* Remove button */}
      <IconButton
        size="small"
        onClick={() => onRemove(index)}
        aria-label={`Remove filter ${index + 1}`}
        sx={{ ml: 'auto', flexShrink: 0 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

function AdvancedFilter({
  columns = [],
  filters = [],
  onFiltersChange,
  anchorEl,
  open,
  onClose,
}) {
  const handleAddFilter = useCallback(() => {
    const defaultField = columns.length > 0 ? columns[0].field : '';
    const newFilter = {
      field: defaultField,
      operator: 'contains',
      value: '',
      value2: '',
    };
    onFiltersChange([...filters, newFilter]);
  }, [columns, filters, onFiltersChange]);

  const handleUpdateFilter = useCallback(
    (index, updatedFilter) => {
      const updated = filters.map((f, i) => (i === index ? updatedFilter : f));
      onFiltersChange(updated);
    },
    [filters, onFiltersChange]
  );

  const handleRemoveFilter = useCallback(
    (index) => {
      const updated = filters.filter((_, i) => i !== index);
      onFiltersChange(updated);
    },
    [filters, onFiltersChange]
  );

  const handleClearAll = useCallback(() => {
    onFiltersChange([]);
  }, [onFiltersChange]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: RADIUS_MD,
            minWidth: 480,
            maxWidth: 680,
            maxHeight: 480,
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${COLOR_BORDER}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <FilterListIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.75rem', flex: 1 }}>
          Advanced Filters
        </Typography>
        {filters.length > 0 && (
          <Button
            size="small"
            onClick={handleClearAll}
            sx={{ fontSize: '0.7rem', textTransform: 'none' }}
          >
            Clear all
          </Button>
        )}
      </Box>

      {/* Filter rows */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1,
          py: 1,
        }}
      >
        {filters.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', textAlign: 'center', py: 3, fontSize: '0.8125rem' }}
          >
            No active filters. Click &quot;Add filter&quot; to get started.
          </Typography>
        ) : (
          filters.map((filter, index) => (
            <FilterRow
              key={index}
              filter={filter}
              index={index}
              columns={columns}
              onUpdate={handleUpdateFilter}
              onRemove={handleRemoveFilter}
            />
          ))
        )}
      </Box>

      {/* Footer with add button */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${COLOR_BORDER}`,
          display: 'flex',
          justifyContent: 'flex-start',
        }}
      >
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddFilter}
          sx={{ fontSize: '0.75rem', textTransform: 'none' }}
        >
          Add filter
        </Button>
      </Box>
    </Popover>
  );
}

export default AdvancedFilter;
